/**
 * PTE Full Mock Page — /pte/mock/:variant
 *
 * Continuous three-section simulation (Speaking & Writing → Reading →
 * Listening) reusing the Alpha renderers. Server-authoritative timing is
 * driven per-section by /api/pte/mock/state/{id}.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import RENDERERS from '@/components/pte/PTERenderers';
import { Loader2, ChevronRight, AlertCircle, CheckCircle2, Sparkles, Info, Timer, Award } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ax = axios.create({ baseURL: BACKEND, withCredentials: true });

const SECTION_LABEL = {
  speaking_writing: 'Speaking & Writing',
  reading: 'Reading',
  listening: 'Listening',
};

const fmtTime = (s) => {
  const m = Math.floor((s || 0) / 60);
  const sec = Math.max(0, (s || 0) % 60);
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const Shell = ({ children }) => (
  <div className="fixed inset-0 z-50 bg-[#0F1720] text-slate-100 overflow-auto" data-testid="pte-mock-shell">
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// Screens
// ---------------------------------------------------------------------------
const StartScreen = ({ variantLabel, onBegin }) => {
  const [accept, setAccept] = useState(false);
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-10" data-testid="mock-info">
      <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 p-6 md:p-8 border border-emerald-800/40">
        <div className="text-xs uppercase tracking-widest text-emerald-300 mb-2 inline-flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5" /> Full mock
        </div>
        <h1 className="text-2xl md:text-3xl font-bold mb-2">{variantLabel || 'Winhouse PTE Academic Mock'}</h1>
        <p className="text-slate-300 text-sm md:text-base mb-4">
          A continuous PTE Academic practice simulation across Speaking &amp; Writing, Reading and Listening.
          Timing is server-authoritative — refresh will not reset your timer.
        </p>
        <div className="grid sm:grid-cols-3 gap-2 mb-4 text-sm">
          <div className="rounded-xl bg-white/5 border border-white/10 p-3"><div className="text-emerald-300 text-[11px] uppercase mb-1">Section 1</div>Speaking &amp; Writing · 60 min</div>
          <div className="rounded-xl bg-white/5 border border-white/10 p-3"><div className="text-emerald-300 text-[11px] uppercase mb-1">Section 2</div>Reading · 30 min</div>
          <div className="rounded-xl bg-white/5 border border-white/10 p-3"><div className="text-emerald-300 text-[11px] uppercase mb-1">Section 3</div>Listening · 45 min</div>
        </div>
        <div className="text-[11px] text-slate-400 mb-5">
          Winhouse Tutors provides independent PTE Academic preparation. This is a practice simulation and not an official Pearson PTE test.
        </div>
        <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 mb-4 text-[11px] text-slate-300">
          For the best listening experience, use headphones in a quiet environment.
        </div>
        <label className="flex items-start gap-2 text-sm text-slate-200 mb-5 cursor-pointer">
          <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)}
            data-testid="mock-accept" className="mt-1" />
          <span>I understand this is a Winhouse PTE Academic practice simulation and I will complete it in one sitting.</span>
        </label>
        <button disabled={!accept} onClick={onBegin} data-testid="mock-begin"
          className="px-6 py-3 rounded-full bg-emerald-400 disabled:bg-slate-600 disabled:cursor-not-allowed text-black font-bold text-sm inline-flex items-center gap-2 hover:bg-emerald-300 transition">
          Begin full mock <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const DevPreviewBanner = ({ notice }) => {
  if (!notice) return null;
  return (
    <div className="mx-auto max-w-5xl px-4 md:px-6 pt-3">
      <div className="rounded-xl bg-amber-500/15 border border-amber-500/40 px-3 py-2 text-amber-100 text-xs flex items-start gap-2"
        data-testid="mock-dev-preview-banner">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <span>{notice}</span>
      </div>
    </div>
  );
};

const MockHeader = ({ state }) => (
  <div className="sticky top-0 z-10 bg-[#0F1720]/95 backdrop-blur border-b border-white/10">
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2 text-xs">
        <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-semibold">Winhouse</span>
        <span className="text-slate-400">Full Mock</span>
        <span className="text-slate-500">·</span>
        <span className="text-slate-300 font-semibold" data-testid="mock-section-label">
          Section {(state.section_index || 0) + 1}/{state.section_count} · {SECTION_LABEL[state.current_section] || state.current_section}
        </span>
      </div>
      <div className="flex items-center gap-3 text-xs">
        <span className="text-slate-400">Q {(state.current_index || 0) + 1} of {state.section_question_count}</span>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-800 text-slate-100 font-mono" data-testid="mock-timer">
          <Timer className="h-3 w-3" /> {fmtTime(state.remaining_seconds)}
        </span>
      </div>
    </div>
  </div>
);

const SectionTransition = ({ completed, next, onContinue }) => (
  <div className="min-h-screen flex items-center justify-center px-4" data-testid="mock-section-transition">
    <div className="max-w-lg text-center">
      <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
      <div className="text-[11px] uppercase tracking-widest text-emerald-300 mb-1">{SECTION_LABEL[completed]} complete</div>
      <h2 className="text-2xl font-bold mb-2">Your responses have been saved.</h2>
      <p className="text-slate-300 text-sm mb-6">Next section: <strong>{SECTION_LABEL[next]}</strong></p>
      <button onClick={onContinue} data-testid="mock-continue"
        className="px-6 py-2.5 rounded-full bg-emerald-400 text-black font-bold text-sm inline-flex items-center gap-2 hover:bg-emerald-300 transition">
        Continue to {SECTION_LABEL[next]} <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  </div>
);

const Completion = ({ onView }) => (
  <div className="min-h-screen flex items-center justify-center px-4" data-testid="mock-completion">
    <div className="max-w-lg text-center">
      <Award className="h-14 w-14 text-emerald-400 mx-auto mb-4" />
      <div className="text-[11px] uppercase tracking-widest text-emerald-300 mb-1">Full PTE practice simulation complete</div>
      <h2 className="text-3xl font-bold mb-2">All responses saved.</h2>
      <p className="text-slate-300 text-sm mb-6">Speaking &amp; writing responses will be queued for tutor review; objective items are scored instantly.</p>
      <button onClick={onView} data-testid="mock-view-results"
        className="px-6 py-2.5 rounded-full bg-emerald-400 text-black font-bold text-sm inline-flex items-center gap-2 hover:bg-emerald-300 transition">
        View my results <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  </div>
);

const Results = ({ results, onDone }) => (
  <div className="max-w-5xl mx-auto px-4 md:px-6 py-8" data-testid="mock-results">
    <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-emerald-900 p-6 md:p-8 border border-emerald-800/40">
      <div className="text-[11px] uppercase tracking-widest text-emerald-300">{results.estimated_score_label}</div>
      <div className="mt-1 text-6xl font-black tracking-tight" data-testid="mock-estimated-score">
        {results.estimated_score != null ? results.estimated_score : '—'}
      </div>
      <div className="text-[11px] text-slate-300 mt-3 max-w-lg">{results.estimated_score_disclaimer}</div>
      <div className="mt-1 text-xs text-slate-400">Variant: {results.variant_label}</div>
    </div>
    <div className="grid md:grid-cols-4 gap-3 mt-5">
      {['speaking', 'writing', 'reading', 'listening'].map((s) => {
        const st = (results.per_skill || {})[s] || { correct: 0, total: 0, review: 0 };
        const acc = st.total ? Math.round((st.correct / st.total) * 100) : null;
        return (
          <div key={s} className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid={`mock-skill-${s}`}>
            <div className="text-[11px] uppercase tracking-widest text-slate-400 capitalize">{s}</div>
            <div className="text-3xl font-bold mt-1">{acc != null ? `${acc}%` : '—'}</div>
            <div className="text-[11px] text-slate-400 mt-1">
              {st.total ? `${st.correct}/${st.total} objective` : '0 objective'}
              {st.review ? ` · ${st.review} awaiting review` : ''}
            </div>
          </div>
        );
      })}
    </div>
    {results.review_pending > 0 && (
      <div className="mt-5 rounded-xl bg-amber-500/15 border border-amber-500/40 px-4 py-3 text-amber-100 text-sm flex items-center gap-2">
        <AlertCircle className="h-4 w-4" /> {results.review_pending} responses are queued for tutor review — your final estimate may adjust after feedback.
      </div>
    )}
    <div className="mt-5 grid sm:grid-cols-2 gap-3">
      <div className="rounded-xl bg-white/5 border border-white/10 p-4">
        <div className="text-[11px] uppercase tracking-widest text-emerald-300 mb-1">Strongest area</div>
        <div className="text-lg font-bold capitalize">{results.strongest_skill || '—'}</div>
      </div>
      <div className="rounded-xl bg-white/5 border border-white/10 p-4">
        <div className="text-[11px] uppercase tracking-widest text-amber-300 mb-1">Focus area</div>
        <div className="text-lg font-bold capitalize">{results.weakest_skill || '—'}</div>
      </div>
    </div>
    {results.insights?.length > 0 && (
      <div className="mt-5 rounded-2xl bg-white/5 border border-white/10 p-5">
        <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">Insights</div>
        <ul className="space-y-1 text-sm">
          {results.insights.map((i, idx) => (
            <li key={idx} className="text-slate-200">• {i.message}</li>
          ))}
        </ul>
      </div>
    )}
    <div className="mt-6 flex justify-end">
      <button onClick={onDone} data-testid="mock-done"
        className="px-5 py-2 rounded-full bg-white/10 border border-white/20 text-sm hover:bg-white/20 transition">
        Back to student portal
      </button>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const InProgress = ({ attempt, onComplete }) => {
  const [state, setState] = useState(attempt);
  const [response, setResponse] = useState({});
  const [saveState, setSaveState] = useState('Saved');
  const [transition, setTransition] = useState(null);
  const saveTimer = useRef(null);

  // Immediate authoritative fetch after mount / advance
  const refresh = useCallback(async () => {
    try {
      const r = await ax.get(`/api/pte/mock/state/${attempt.id}`);
      setState(r.data);
    } catch (_) {}
  }, [attempt.id]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const r = await ax.get(`/api/pte/mock/state/${attempt.id}`);
        setState(r.data);
        if (r.data.remaining_seconds <= 0 && r.data.status === 'in_progress') {
          // Server will forward on next advance click; we don't auto-complete here
        }
      } catch (_) {}
    }, 5000);
    return () => clearInterval(id);
  }, [attempt.id]);

  const sect = state.sections?.[state.section_index];
  const qref = sect?.questions?.[state.current_index];
  const q = state.current_question;
  const Renderer = q && RENDERERS[q.question_type];

  const save = useCallback(async () => {
    if (!qref) return;
    setSaveState('Saving…');
    try {
      await ax.post(`/api/pte/mock/state/${attempt.id}/save`, {
        question_id: qref.question_id,
        response: response.response ?? null,
        recording_url: response.recording_url || null,
      });
      setSaveState('Saved');
    } catch { setSaveState('Reconnecting…'); }
  }, [attempt.id, qref, response]);
  useEffect(() => {
    if (!qref) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(save, 900);
    return () => clearTimeout(saveTimer.current);
  }, [response, qref, save]);

  useEffect(() => {
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const next = async () => {
    await save();
    setResponse({});
    try {
      const r = await ax.post(`/api/pte/mock/state/${attempt.id}/advance`);
      if (r.data.mock_completed) {
        onComplete(attempt.id);
      } else if (r.data.section_transition) {
        setTransition({ completed: r.data.completed_section, next: r.data.next_section });
      } else {
        refresh();
      }
    } catch { setSaveState('Reconnecting…'); }
  };

  if (transition) {
    return (
      <SectionTransition completed={transition.completed} next={transition.next}
        onContinue={async () => { setTransition(null); await refresh(); }} />
    );
  }
  if (!q) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
        <div className="text-sm">Preparing your next task…</div>
      </div>
    </div>
  );
  return (
    <div>
      <MockHeader state={state} />
      <DevPreviewBanner notice={state.dev_preview_notice} />
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
        <div className="rounded-2xl bg-white text-slate-900">
          {Renderer ? <Renderer question={q} onResponseChange={setResponse} disabled={false} /> : (
            <div className="p-6 text-slate-500">This task is not yet available.</div>
          )}
        </div>
        <div className="mt-6 flex items-center justify-between">
          <div className="text-[11px] text-slate-400" data-testid="mock-save-state">{saveState}</div>
          <button onClick={next} data-testid="mock-next"
            className="px-6 py-2.5 rounded-full bg-emerald-400 text-black font-bold text-sm inline-flex items-center gap-2 hover:bg-emerald-300 transition">
            Next task <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function PTEMockPage() {
  const { variant } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const attemptParam = searchParams.get('attempt');
  const [phase, setPhase] = useState('info');            // info | in_progress | complete | results
  const [attempt, setAttempt] = useState(null);
  const [variantLabel, setVariantLabel] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await ax.get('/api/pte/mock/variants');
        const v = (r.data.items || []).find((it) => it.id === variant);
        if (v) setVariantLabel(v.label);
      } catch (_) {}
    })();
  }, [variant]);

  // Deep link: view=results&attempt=<id>
  useEffect(() => {
    if (!viewParam) return;
    (async () => {
      try {
        if (viewParam === 'results' && attemptParam) {
          const r = await ax.get(`/api/pte/mock/state/${attemptParam}`);
          if (r.data.status === 'completed' && r.data.results) {
            setResults(r.data.results); setPhase('results');
          } else {
            setAttempt(r.data); setPhase('in_progress');
          }
        } else if (viewParam === 'resume') {
          const r = await ax.get('/api/pte/mock/resume');
          if (r.data.resumable && r.data.attempt) {
            setAttempt(r.data.attempt); setPhase('in_progress');
          }
        }
      } catch (_) {}
    })();
  }, [viewParam, attemptParam]);

  const begin = useCallback(async () => {
    try {
      const r = await ax.post('/api/pte/mock/start', { variant });
      setAttempt(r.data); setPhase('in_progress');
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401 || s === 403) setError('Please sign in as a student to start a Full Mock.');
      else if (s === 409) setError('Not enough mock-eligible questions in the bank for this variant. Please contact your admin.');
      else setError('We could not start your mock right now. Please try again in a moment.');
    }
  }, [variant]);

  const complete = useCallback(async (id) => {
    try {
      const r = await ax.post(`/api/pte/mock/state/${id}/complete`);
      setResults(r.data.results); setPhase('complete');
    } catch { setPhase('complete'); }
  }, []);

  if (error) return (
    <Shell>
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <AlertCircle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
        <div className="text-slate-200 mb-4" data-testid="mock-error-msg">{error}</div>
        <div className="flex justify-center gap-3">
          <button onClick={() => navigate('/student/login')}
            className="px-4 py-2 rounded-full bg-emerald-400 text-black text-sm font-bold">Sign in</button>
          <button onClick={() => navigate('/resources/pte-academic')}
            className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm">Back</button>
        </div>
      </div>
    </Shell>
  );
  return (
    <Shell>
      {phase === 'info' && <StartScreen variantLabel={variantLabel} onBegin={begin} />}
      {phase === 'in_progress' && attempt && <InProgress attempt={attempt} onComplete={complete} />}
      {phase === 'complete' && <Completion onView={() => setPhase('results')} />}
      {phase === 'results' && results && <Results results={results} onDone={() => navigate('/student/dashboard')} />}
    </Shell>
  );
}
